import { Card, ErrorView, OnboardingLoading, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Button, Label, Textbox, useAuth } from '@app/engine';
import { CommonConstants, FormatLocalized, GetSupportedCountriesList, GetTimezoneList, IsValidE164, Logger, NormalizePhoneE164 } from '@shared/common';
import { BillingClient, GetUserGeoCountry, GetUserGeoTimezone, NicheContentClient, Telemetry, TenantClient, UserClient } from '@shared/frontend';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { INicheContentDTO, IPlanDTO } from '@shared/common';

/** File-local constants for the Onboarding wizard. */
const localConstants = {
  logContext: 'AccountStep',
  emptyPlans: [] as IPlanDTO[],
  emptyNiches: [] as INicheContentDTO[],
  // Staged signup-session state keys (mirror the landing SignupCard) used to prefill the form.
  stateKeyBusiness: 'business',
  stateKeyContactName: 'contactName',
  stateKeyContactPhone: 'contactPhone',
  stateKeyTrade: 'trade',
  stateKeyPlan: 'plan',
  stateKeyAreaCodes: 'areaCodes',
  stateKeyCountryCode: 'countryCode',
  stateKeyTimezone: 'timezone',
  // Character cap on the preferred-area-codes input, matching the landing SignupCard: room for four 3-digit codes with ", " separators (e.g. "415, 628, 510, 214").
  areaCodesMaxLength: 18
} as const;

/** Collected onboarding form values. */
interface IOnboardingForm {
  businessName: string;
  contactName: string;
  contactPhone: string;
  niche: string;
  planCode: string;
  areaCodes: string;
  countryCode: string;
  timezone: string;
}

/** Serialised shape stored in sessionStorage so the landing and onboarding forms stay in sync across navigation. */
interface ISignupDraft {
  businessName: string;
  contactName: string;
  contactPhone: string;
  niche: string;
  planCode: string;
  areaCodes: string;
  countryCode: string;
  timezone: string;
}

/** Supported country codes for the country select. */
const supportedCountryCodes = GetSupportedCountriesList().map((c) => c.code);
/** The default country code, derived from the browser or falling back to US. */
const defaultCountryCode = supportedCountryCodes.includes(GetUserGeoCountry()) ? GetUserGeoCountry() : (supportedCountryCodes[0] ?? 'US');
/** The default timezone, derived from the browser or falling back to the system default. */
const defaultTimezone = GetUserGeoTimezone();

/** The empty onboarding form. */
const emptyForm: IOnboardingForm = { businessName: '', contactName: '', contactPhone: '', niche: '', planCode: '', areaCodes: '', countryCode: defaultCountryCode, timezone: defaultTimezone };

/**
 * Reads the shared signup draft from session storage, returning null when absent or corrupt.
 *
 * @returns The parsed draft, or null.
 */
function ReadSignupDraft(): ISignupDraft | null {
  try {
    const raw = sessionStorage.getItem(Constants.SignupDraftStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ISignupDraft>;
    return {
      businessName: typeof parsed.businessName === 'string' ? parsed.businessName : '',
      contactName: typeof parsed.contactName === 'string' ? parsed.contactName : '',
      contactPhone: typeof parsed.contactPhone === 'string' ? parsed.contactPhone : '',
      niche: typeof parsed.niche === 'string' ? parsed.niche : '',
      planCode: typeof parsed.planCode === 'string' ? parsed.planCode : '',
      areaCodes: typeof parsed.areaCodes === 'string' ? parsed.areaCodes : '',
      countryCode: typeof parsed.countryCode === 'string' ? parsed.countryCode : '',
      timezone: typeof parsed.timezone === 'string' ? parsed.timezone : ''
    };
  } catch (err: unknown) {
    Logger.Error(localConstants.logContext, `Failed to read signup draft: ${Logger.GetErrorMessage(err)}`);
    return null;
  }
}

/**
 * Writes the shared signup draft to session storage.
 *
 * @param draft - The draft to persist.
 */
function WriteSignupDraft(draft: ISignupDraft): void {
  try {
    sessionStorage.setItem(Constants.SignupDraftStorageKey, JSON.stringify(draft));
  } catch (err: unknown) {
    Logger.Error(localConstants.logContext, `Failed to write signup draft: ${Logger.GetErrorMessage(err)}`);
  }
}

/** Clears the shared signup draft from session storage. */
function ClearSignupDraft(): void {
  try {
    sessionStorage.removeItem(Constants.SignupDraftStorageKey);
  } catch (err: unknown) {
    Logger.Error(localConstants.logContext, `Failed to clear signup draft: ${Logger.GetErrorMessage(err)}`);
  }
}

/**
 * Builds the initial form state using the fallback chain: local draft -> server signup session -> geo defaults -> hard defaults.
 *
 * @param state - The server-side signup session state, if any.
 * @returns The resolved initial form.
 */
function BuildInitialForm(state: Record<string, string> | null | undefined): IOnboardingForm {
  const draft = ReadSignupDraft();
  return {
    businessName: draft?.businessName ?? state?.[localConstants.stateKeyBusiness] ?? emptyForm.businessName,
    contactName: draft?.contactName ?? state?.[localConstants.stateKeyContactName] ?? emptyForm.contactName,
    contactPhone: draft?.contactPhone ?? state?.[localConstants.stateKeyContactPhone] ?? emptyForm.contactPhone,
    niche: draft?.niche ?? state?.[localConstants.stateKeyTrade] ?? emptyForm.niche,
    planCode: draft?.planCode ?? state?.[localConstants.stateKeyPlan] ?? emptyForm.planCode,
    areaCodes: draft?.areaCodes ?? state?.[localConstants.stateKeyAreaCodes] ?? emptyForm.areaCodes,
    countryCode: draft?.countryCode ?? state?.[localConstants.stateKeyCountryCode] ?? emptyForm.countryCode,
    timezone: draft?.timezone ?? state?.[localConstants.stateKeyTimezone] ?? emptyForm.timezone
  };
}

/**
 * Account step of the onboarding wizard: the first gated step. Collects the minimum business details and creates our own data only — tenant, owner, and business profile — via the user service's
 * onboarding/account endpoint. No vendor (Stripe/Vapi) call happens here; billing verification and phone provisioning are later gated steps. On success it stashes the selected plan and preferred
 * area code for those later steps, then calls onComplete so the wizard re-resolves progress and advances to billing. The step is re-entrant server-side: re-submitting for an existing account
 * returns the current progress unchanged.
 *
 * @param props - Component props.
 * @param props.onComplete - Invoked once the account exists so the wizard advances to the next step.
 * @returns The account-step panel element.
 */
export function AccountStep({ onComplete }: { onComplete: () => void }) {
  const { user, logout } = useAuth();
  const { selectedLang } = useLocale();
  const { contactEmail } = useLobbySettings();
  const navigate = useNavigate();

  const plansResult = useDashboardData<IPlanDTO[]>((signal) => BillingClient.ListPublicPlans(signal, selectedLang), [selectedLang]);
  const plans = plansResult.data ?? localConstants.emptyPlans;
  const nichesResult = useDashboardData<INicheContentDTO[]>((signal) => NicheContentClient.ListPublicNiches(signal, selectedLang), [selectedLang]);
  const niches = nichesResult.data ?? localConstants.emptyNiches;

  const [form, setForm] = useState<IOnboardingForm>(emptyForm);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Best-effort prefill from the shared local draft, then the staged signup session captured on the marketing landing page. The local draft wins so navigation back and forth between landing and
  // onboarding does not reset edits the user has already made.
  useEffect(() => {
    const email = user?.email;
    if (!email) return;
    const controller = new AbortController();
    UserClient.GetSignup(controller.signal, email)
      .then((session) => {
        setForm(BuildInitialForm(session?.state ?? null));
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        // On failure fall back to the local draft / geo defaults so the form is still usable.
        setForm(BuildInitialForm(null));
      });
    return () => controller.abort();
  }, [user?.email]);

  const update = (key: keyof IOnboardingForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => {
      const next = { ...prev, [key]: event.target.value };
      WriteSignupDraft(next);
      return next;
    });
    setError('');
  };

  const updateValue = (key: keyof IOnboardingForm) => (value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      WriteSignupDraft(next);
      return next;
    });
    setError('');
  };

  // Stage the chosen country immediately (not just on submit) so the public landing pricing reflects it mid-onboarding, before the account and its billing currency exist. Mirrors the country
  // persistence in onSubmit that the billing step consumes.
  const onCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const code = event.target.value;
    setForm((prev) => {
      const next = { ...prev, countryCode: code };
      WriteSignupDraft(next);
      return next;
    });
    setError('');
    if (code.trim().length > 0) sessionStorage.setItem(Constants.SetupBillingCountryStorageKey, code.trim());
    else sessionStorage.removeItem(Constants.SetupBillingCountryStorageKey);
  };

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (
      form.businessName.trim().length === 0 ||
      form.contactName.trim().length === 0 ||
      form.contactPhone.trim().length === 0 ||
      form.niche.length === 0 ||
      form.planCode.length === 0 ||
      form.areaCodes.trim().length === 0 ||
      form.countryCode.length === 0 ||
      form.timezone.length === 0
    ) {
      setError(Localized.OnboardingErrorRequired);
      return;
    }
    if (!IsValidE164(NormalizePhoneE164(form.contactPhone))) {
      setError(Localized.OnboardingErrorPhone);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const controller = new AbortController();
      await TenantClient.ProvisionAccount(controller.signal, {
        businessName: form.businessName.trim(),
        displayName: form.contactName.trim(),
        phoneE164: NormalizePhoneE164(form.contactPhone),
        niche: form.niche,
        planCode: form.planCode,
        countryCode: form.countryCode.trim() || undefined,
        timezone: form.timezone.trim() || undefined
      });
      // Hand the selected plan to the billing step (it starts the Stripe checkout) and the preferred area codes to the phone step (an AI Number area-code hint). The area codes are NOT part of the
      // account payload — they are only consumed when the gated phone step provisions the AI Number.
      sessionStorage.setItem(Constants.SetupBillingPlanStorageKey, form.planCode);
      // Stash the selected country so the billing step can show the plan price in the tenant's billing currency (the subscription that carries the settlement currency does not exist yet).
      if (form.countryCode.trim().length > 0) sessionStorage.setItem(Constants.SetupBillingCountryStorageKey, form.countryCode.trim());
      else sessionStorage.removeItem(Constants.SetupBillingCountryStorageKey);
      if (form.areaCodes.trim().length > 0) sessionStorage.setItem(Constants.OnboardingAreaCodeStorageKey, form.areaCodes.trim());
      else sessionStorage.removeItem(Constants.OnboardingAreaCodeStorageKey);
      // The account now exists; the shared draft is no longer needed and must not pollute a future signup.
      ClearSignupDraft();
      Telemetry.OnboardingStepCompleted(CommonConstants.Database.OnboardingStep.Account);
      onComplete();
    } catch (err: unknown) {
      // The account must be created (tenant, owner, profile) before the wizard advances. A failure here is NOT silently recoverable: surface an actionable error and keep the owner on this step to
      // retry, rather than slipping into a half-provisioned state.
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setError(FormatLocalized(Localized.OnboardingError, contactEmail));
      setSubmitting(false);
    }
  };

  // Abandons onboarding: signs the visitor out and returns them to the public landing page.
  const onLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    }
    navigate(Constants.Route.Landing);
  }, [logout, navigate]);

  if (plansResult.loading || nichesResult.loading) return <OnboardingLoading title={Localized.OnboardingTitle} />;
  if (plansResult.error || nichesResult.error) {
    return (
      <ErrorView
        onRetry={() => {
          plansResult.reload();
          nichesResult.reload();
        }}
      />
    );
  }

  return (
    <Card title={Localized.OnboardingTitle} className={Styles.onboardingCard}>
      <Label classNameContainer={Styles.muted} text={Localized.OnboardingSubtitle} />
      <form onSubmit={onSubmit}>
        <div className={Styles.formGrid}>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedBusinessName} />
            <Textbox value={form.businessName} placeholder={Localized.OnboardingBusinessNamePlaceholder} onChange={updateValue('businessName')} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.OnboardingContactName} />
            <Textbox value={form.contactName} placeholder={Localized.OnboardingContactNamePlaceholder} onChange={updateValue('contactName')} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedContactPhone} />
            <Textbox
              value={form.contactPhone}
              type="tel"
              placeholder={Localized.SharedPhoneExample}
              onChange={updateValue('contactPhone')}
              onBlur={() => setForm((prev) => ({ ...prev, contactPhone: NormalizePhoneE164(prev.contactPhone) }))}
              classNameText={Styles.input}
            />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedIndustry} />
            <select id="onboarding-niche" className={Styles.input} value={form.niche} onChange={update('niche')}>
              <option value="">{Localized.OnboardingNichePlaceholder}</option>
              {niches.map((entry) => (
                <option key={entry.niche} value={entry.niche}>
                  {entry.signupLabel}
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedPlan} />
            <select id="onboarding-plan" className={Styles.input} value={form.planCode} onChange={update('planCode')}>
              <option value="">{Localized.OnboardingPlanPlaceholder}</option>
              {/* Names only: plan pricing lives on the landing pricing card and the dashboard billing panel, so the onboarding selector stays currency-agnostic. */}
              {plans.map((plan) => (
                <option key={plan.code} value={plan.code}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.OnboardingAreaCodes} />
            <Textbox
              value={form.areaCodes}
              placeholder={Localized.OnboardingAreaCodesPlaceholder}
              onChange={updateValue('areaCodes')}
              classNameText={Styles.input}
              maxLength={localConstants.areaCodesMaxLength}
            />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.OnboardingCountry} />
            <select id="onboarding-country" className={Styles.input} value={form.countryCode} onChange={onCountryChange}>
              {GetSupportedCountriesList().map((country) => (
                <option key={country.code} value={country.code}>
                  {(Localized as Record<string, string>)[country.labelKey] ?? country.code}
                </option>
              ))}
            </select>
          </div>
          <div className={`${Styles.field} ${Styles.fieldSpanEnd}`}>
            <Label classNameContainer={Styles.label} text={Localized.OnboardingTimezone} />
            <select id="onboarding-timezone" className={Styles.input} value={form.timezone} onChange={update('timezone')}>
              {GetTimezoneList().map((tz) => (
                <option key={tz.iana} value={tz.iana}>
                  {(Localized as Record<string, string>)[tz.labelKey] ?? tz.iana}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error.length > 0 ? <Label classNameContainer={Styles.errorText} text={error} /> : null}

        <div className={Styles.formActionsEnd}>
          <Button text={Localized.DashSignOut} type="button" disabled={submitting} classNameButtonEnabled={Styles.btn} classNameButtonDisabled={Styles.btn} onClickHandler={() => void onLogout()} />
          <Button
            text={submitting ? Localized.OnboardingSubmitting : Localized.SharedCreateAccount}
            disabled={submitting}
            classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`}
            classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary}`}
          />
        </div>
      </form>
    </Card>
  );
}
